import ComboFilters from "pages/Reports/ComboFilters";
import { getAllMonthsUntilNow } from "pages/Reports/Reports";
import { ReportsAPI } from "providers/ReportsAPI";
import { useEffect, useState } from "react";
import { If } from "shared/components/control-statements";
import { useDragScroll } from "shared/hooks/useDragScroll";
import { Currency } from "shared/utils/Currency";
import { Numbers } from "shared/utils/Numbers";
import styles from "styles/pages/reports/reports.module.scss";
import classNames from "classnames/bind";
import { Loading } from "@components";
const css = classNames.bind(styles);

const Trend = ({ growth }) => {
  const isNegative = growth < 0;
  const iconClass = isNegative
    ? "fas fa-arrow-trend-down"
    : "far fa-arrow-trend-up";
  const iconColor = isNegative ? "#e48186" : "#51D9A3";
  const sign = growth > 0 ? "+" : "";

  if (+growth === 0 || !growth) return null;

  return (
    <div className={css({ growth: true, decrease: isNegative })}>
      {sign}
      {Numbers.number(growth, 2)}%{" "}
      <i className={iconClass} style={{ color: iconColor }} />
    </div>
  );
};

const buildTable = (data) => {
  const statuses = {
    novos: `Clientes novos`,
    onboarding: `Clientes em onboarding`,
    engajados: `Clientes engajados`,
    atrasados: `Clientes atrasados`,
  };

  const rows = ["novos", "onboarding", "engajados", "atrasados"];
  const columns = ["interval", "ticket", "order_per_client", "total"];

  const formatValue = (column, value) => {
    if (value === undefined) return "-";
    switch (column) {
      case "recency":
      case "interval":
        return `${value} dias`;
      case "ticket":
      case "total":
        return Currency.formatCurrency(value);
      case "order_per_client":
        return Number(value) % 1 === 0
          ? `${Number(value)}`
          : Number(value).toFixed(1);
      default:
        return value;
    }
  };

  const table = rows.map((row) => {
    return {
      alias: row,
      status: statuses[row],
      values: columns.map((column) => {
        const rawValue = data?.[column]?.[row]?.value ?? 0;
        const growth = data?.[column]?.[row]?.growth ?? 0;

        return {
          tuple: `${row}__${column}`,
          value: rawValue,
          formatted: formatValue(column, rawValue),
          growth,
        };
      }),
    };
  });

  return table;
};

export default function StageDetails() {
  const months = getAllMonthsUntilNow(false);

  const [selectedMonth, setSelectedMonth] = useState(months[0]);
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const provider = new ReportsAPI();
      const response = await provider.lifeCycle(
        "details",
        selectedMonth.year,
        selectedMonth.month
      );
      setRawData(response);
      setLoading(false);
    };

    fetchData();
  }, [selectedMonth]);

  const { scrollRef, onMouseDown } = useDragScroll();

  return (
    <div className={styles.box2}>
      <div className="d-flex align-center justify-between px-15">
        <div className="d-flex align-center relative gap-5">
          <p className={styles.label}>Detalhamento de estágios</p>
          <span
            className={styles.info}
            title="Compare recência, frequência, valor, quantidade de pedidos e
              faturamento de cada estágio do ciclo de vida."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>
        <div className="d-flex align-center">
          <ComboFilters
            options={months}
            onSelect={(option) => setSelectedMonth(option)}
            selectedOption={selectedMonth}
          />
        </div>
      </div>

      <div className={styles.hairline} />

      {loading ? (
        <div
          className="d-flex align-center justify-center"
          style={{ height: 225 }}
        >
          <Loading />
        </div>
      ) : (
        <div className={styles.table} onMouseDown={onMouseDown} ref={scrollRef}>
          <table className={styles.rfv}>
            <thead>
              <tr>
                <th>ESTÁGIO</th>
                {/* <th>
                  <div className="d-flex align-center gap-5 relative">
                    ÚLTIMO PEDIDO HÁ
                    <span
                      className={styles.info}
                      title="Mediana de tempo sem pedir."
                    >
                      <i className="fal fa-info-circle" />
                    </span>
                  </div>
                </th> */}
                <th>
                  <div className="d-flex align-center gap-5 relative">
                    INTERVALO ENTRE COMPRAS{" "}
                    <span
                      className={styles.info}
                      title="Média de intervalo entre compras."
                    >
                      <i className="fal fa-info-circle" />
                    </span>
                  </div>
                </th>
                <th>
                  <div className="d-flex align-center gap-5 relative">
                    TICKET MÉDIO{" "}
                    <span className={styles.info} title="Mediana do ticket.">
                      <i className="fal fa-info-circle" />
                    </span>
                  </div>
                </th>
                <th>
                  <div className="d-flex align-center gap-5 relative">
                    PEDIDOS P/ CLIENTE{" "}
                    <span
                      className={styles.info}
                      title="Média de pedidos por cliente."
                    >
                      <i className="fal fa-info-circle" />
                    </span>
                  </div>
                </th>
                <th>
                  <div className="d-flex align-center gap-5 relative">
                    FATURAMENTO MÉDIO
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ height: 15 }} />
              {buildTable(rawData).map((row, idx) => (
                <tr key={row.status}>
                  <td
                    className={styles.value}
                    style={{
                      textAlign: "left",
                      paddingLeft: 15,
                      minWidth: 230,
                    }}
                    key={idx}
                  >
                    {row.status}
                  </td>
                  {row.values.map(
                    ({ tuple, value, formatted, growth }, idx) => {
                      return (
                        <td
                          className={styles.value}
                          key={idx}
                          style={{ minWidth: 230 }}
                        >
                          <div className="d-flex items-center gap-5">
                            {formatted ?? "---"}
                            <If cond={growth}>
                              <Trend value={growth} />
                            </If>
                          </div>
                        </td>
                      );
                    }
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
